import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { OPEN_ASSISTANT_EVENT, fetchAiStatus } from './assistantApi';
import {
  ASSISTANT_DOCK_MQ,
  applyDockWidthVar,
  loadDockWidth,
  saveDockWidth,
  clampDockWidth,
  isAssistantHomePath,
} from './assistantLayout';

const AssistantContext = createContext(null);

function useDesktopDock() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia(ASSISTANT_DOCK_MQ).matches;
  });
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(ASSISTANT_DOCK_MQ);
    const onChange = () => setIsDesktop(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    onChange();
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isDesktop;
}

export function AssistantProvider({ children }) {
  const auth = useAuth() || {};
  const authReady = Boolean(auth.authReady);
  const uid = auth.currentUser?.uid || '';
  const pathname = useLocation()?.pathname || '';
  const onHome = isAssistantHomePath(pathname);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('troubleshoot');
  const [pins, setPins] = useState({});
  const [pagePins, setPagePinsState] = useState({});
  const pagePinsRef = useRef({});
  const followLockRef = useRef(null);
  const [seedMessage, setSeedMessage] = useState('');
  const [status, setStatus] = useState({ enabled: false });
  const [dockWidth, setDockWidthState] = useState(loadDockWidth);
  const isDesktop = useDesktopDock();

  const setPagePins = useCallback((next) => {
    const nextPins = next || {};
    pagePinsRef.current = nextPins;
    setPagePinsState(nextPins);
  }, []);

  const setDockWidth = useCallback((next) => {
    const width = clampDockWidth(next);
    setDockWidthState(width);
    saveDockWidth(width);
  }, []);

  const openAssistantPanel = useCallback((opts = {}) => {
    if (onHome) return;
    if (status.enabled !== true) return;
    const canSetup = status.canSetup === true;
    const canTroubleshoot = status.canTroubleshoot === true;
    let nextMode = opts.mode;
    if (nextMode === 'setup' && !canSetup) {
      nextMode = canTroubleshoot ? 'troubleshoot' : null;
    }
    if (nextMode === 'troubleshoot' && !canTroubleshoot) {
      nextMode = canSetup ? 'setup' : null;
    }
    if (!nextMode) {
      if (canTroubleshoot) nextMode = 'troubleshoot';
      else if (canSetup) nextMode = 'setup';
      else return;
    }
    if (nextMode === 'setup' || nextMode === 'troubleshoot') setMode(nextMode);
    if (Object.prototype.hasOwnProperty.call(opts, 'pins')) {
      followLockRef.current = JSON.stringify(pagePinsRef.current || {});
      setPins(opts.pins || {});
    } else {
      followLockRef.current = null;
      setPins(pagePinsRef.current || {});
    }
    if (opts.seedMessage) setSeedMessage(opts.seedMessage);
    setIsOpen(true);
  }, [status, onHome]);

  const closeAssistant = useCallback(() => {
    setIsOpen(false);
    setSeedMessage('');
  }, []);

  const toggleAssistant = useCallback(() => {
    if (isOpen) closeAssistant();
    else openAssistantPanel({});
  }, [isOpen, closeAssistant, openAssistantPanel]);

  const clearPins = useCallback(() => {
    followLockRef.current = JSON.stringify(pagePinsRef.current || {});
    setPins({});
  }, []);

  const setAuthorizedMode = useCallback((next) => {
    if (next === 'setup' && status.canSetup !== true) return;
    if (next === 'troubleshoot' && status.canTroubleshoot !== true) return;
    if (next === 'setup' || next === 'troubleshoot') setMode(next);
  }, [status]);

  useEffect(() => {
    const onOpen = (e) => openAssistantPanel(e.detail || {});
    window.addEventListener(OPEN_ASSISTANT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, onOpen);
  }, [openAssistantPanel]);

  useEffect(() => {
    if (!onHome) return;
    setIsOpen(false);
    setSeedMessage('');
  }, [onHome]);

  useEffect(() => {
    if (!isOpen) return;
    const serialized = JSON.stringify(pagePins || {});
    if (followLockRef.current != null && followLockRef.current === serialized) return;
    followLockRef.current = null;
    setPins(pagePins || {});
  }, [pagePins, isOpen]);

  useEffect(() => {
    applyDockWidthVar(isOpen && isDesktop ? dockWidth : 0);
    return () => applyDockWidthVar(0);
  }, [isOpen, isDesktop, dockWidth]);

  useEffect(() => {
    if (!authReady) return undefined;
    if (!uid) {
      setStatus({ enabled: false });
      return undefined;
    }
    let cancelled = false;
    fetchAiStatus().then((s) => {
      if (!cancelled) {
        const next = s || { enabled: false };
        setStatus({
          enabled: next.enabled === true,
          canSetup: next.canSetup === true,
          canTroubleshoot: next.canTroubleshoot === true,
          applyEnabled: false,
          writeTools: Array.isArray(next.writeTools) ? next.writeTools : [],
          role: next.role || '',
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authReady, uid]);

  const value = useMemo(
    () => ({
      isOpen,
      mode,
      setMode: setAuthorizedMode,
      pins,
      setPins,
      pagePins,
      setPagePins,
      clearPins,
      seedMessage,
      setSeedMessage,
      status,
      dockWidth,
      setDockWidth,
      isDesktop,
      openAssistant: openAssistantPanel,
      closeAssistant,
      toggleAssistant,
    }),
    [
      isOpen,
      mode,
      pins,
      pagePins,
      seedMessage,
      status,
      dockWidth,
      isDesktop,
      openAssistantPanel,
      closeAssistant,
      toggleAssistant,
      setPagePins,
      setAuthorizedMode,
      clearPins,
      setDockWidth,
    ]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant() {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within AssistantProvider');
  return ctx;
}

export function useAssistantOptional() {
  return useContext(AssistantContext);
}

/** Pages register the object currently on screen so the FAB can pin it. */
export function useAssistantPagePins(pins) {
  const assistant = useAssistantOptional();
  const setPagePins = assistant?.setPagePins;
  const serialized = JSON.stringify(pins || {});
  useEffect(() => {
    if (!setPagePins) return undefined;
    setPagePins(JSON.parse(serialized));
    return () => setPagePins({});
  }, [setPagePins, serialized]);
}

export default AssistantContext;
